import { useQuery } from "react-query";
import { firestore } from "@/services/firestore";
import { CustomerUpdate } from "@/types/v2";

interface UseLatestCustomerUpdateByCardIdParams {
  shopId: string;
  cardId: string;
  enabled?: boolean;
}

export const useLatestCustomerUpdateByCardId = ({
  shopId,
  cardId,
  enabled = true,
}: UseLatestCustomerUpdateByCardIdParams) => {
  return useQuery({
    queryKey: ["latest-customerUpdate-by-cardId", shopId, cardId],
    queryFn: async (): Promise<CustomerUpdate | null> => {
      if (!shopId || !cardId) {
        return null;
      }

      // Query for customer updates with the specific cardId, ordered by createdAt descending (latest first)
      const result =
        await firestore.paginateDocumentsWithFilters<CustomerUpdate>(
          `/shops/${shopId}/customerUpdates`,
          1, // We only need the latest one
          undefined, // No cursor needed since we only want the first result
          undefined, // No converter
          "createdAt", // Sort by createdAt
          "desc", // Descending order to get latest first
          [
            {
              searchParam: "cardId",
              opsString: "==",
              equality: cardId,
            },
          ]
        );

      // Return the first (latest) customer update or null if none found
      return result.docs.length > 0 ? (result.docs[0] as CustomerUpdate) : null;
    },
    enabled: enabled && !!shopId && !!cardId,
    staleTime: 5 * 1000, // Cache for 5 seconds
    cacheTime: 20 * 1000, // Keep in cache for 20 seconds
  });
};
